import { useEffect, useRef, useState } from 'react';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import OutlinedInput from '@mui/material/OutlinedInput';
import RadioGroup from '@mui/material/RadioGroup';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormLabel from '@mui/material/FormLabel';
import Radio from '@mui/material/Radio';
import { ApiClientStatusEnum } from '../../utils/api-client';
import { type OAuthIntegrationResponse, useIntegrationsApiHook } from './cloudflex-api.hook';
import CircularProgress from '@mui/material/CircularProgress';
import Alert from '@mui/material/Alert';
import { API } from 'Amplify';
import TextField from '@mui/material/TextField';

type Fleet = {
  thingGroupName: string;
  thingGroupId: string;
};

type FleetActionsCloudflexProps = {
  fleets: Fleet[] | null;
};

type CreateCloudFlexDeviceProps = {
  ean: string;
  settings: any;
  cfu: Record<string, any>;
  targetFleet: string;
  name: string;
};

type CreateCloudFlexRequest = {
  devices: CreateCloudFlexDeviceProps[];
};

type CreateCloudFlexResponse = {
  devices: {
    settings: Record<string, any>;
    thingName: string;
    ean?: string;
    status: boolean;
  }[];
};

export function FleetsActionsCloudflex(props: FleetActionsCloudflexProps) {
  const [isShowCloudflexDialog, setShowCloudflexDialog] = useState(false);
  const [selectedFleet, setSelectedFleet] = useState('');
  const [deviceName, setDeviceName] = useState<string | undefined>(undefined);
  const [isCfCreationInProgress, setCfCreationInProgress] = useState<boolean>(false);
  const [cfCreationError, setCfCreationError] = useState<string | undefined>(undefined);
  const [newlyCreatedCfId, setNewlyCreatedCfId] = useState<string | undefined>(undefined);

  const [selectedIntegrationOption, setSelectedIntegrationOption] = useState('');
  const { integrationsListApiClientStatus, fetchIntegrationsList, triggerIntegrationsList } = useIntegrationsApiHook();
  const selectedIntegration = useRef<OAuthIntegrationResponse | null>(null);

  useEffect(() => {
    // This forces a dummy list to be returned.
    // We don't want to use the return URIs, only the list of supported integrations
    fetchIntegrationsList('noname');
  }, []);

  const handleIntegrationSelection = (event: any) => {
    setSelectedIntegrationOption(event.target.value);

    const integration = (integrationsListApiClientStatus.data || []).find((item) => item.brand === event.target.value);
    if (integration) {
      selectedIntegration.current = integration;
    } else {
      selectedIntegration.current = null;
    }
  };

  const createNewCloudFlexDevice = async (integration: OAuthIntegrationResponse) => {
    const request: CreateCloudFlexRequest = {
      devices: [{ ean: '0000', settings: {}, cfu: { [integration.brand!]: {} }, targetFleet: selectedFleet, name: deviceName! }],
    };

    try {
      setCfCreationInProgress(true);
      setCfCreationError(undefined);
      setNewlyCreatedCfId(undefined);

      const res: CreateCloudFlexResponse = await API.post('IO_API', `/devices`, { body: request });
      if (res.devices?.length > 0) {
        setNewlyCreatedCfId(res.devices[0].thingName);
      }
    } catch (e: any) {
      console.error(e);
      setCfCreationError('Failed to create CloudFlex device. Please try again.');
    }

    setCfCreationInProgress(false);
    setDeviceName(undefined);
  };

  let PanelContent: JSX.Element | null = null;
  let isDeviceCreationAllowed = false;

  if (isCfCreationInProgress) {
    PanelContent = (
      <div className="fleet-actions__cloudflex-loading-panel">
        <CircularProgress />
        <span className="cloudflex-loading-label">Creating new CloudFlex device...</span>
      </div>
    );
  } else if (
    integrationsListApiClientStatus.clientStatus === ApiClientStatusEnum.Busy ||
    integrationsListApiClientStatus.clientStatus === ApiClientStatusEnum.Idle
  ) {
    PanelContent = (
      <div className="fleet-actions__cloudflex-loading-panel">
        <CircularProgress />
        <span className="cloudflex-loading-label">Loading CloudFlex integrations...</span>
      </div>
    );
  } else if (integrationsListApiClientStatus.clientStatus === ApiClientStatusEnum.Error) {
    PanelContent = (
      <div className="fleet-actions__cloudflex-panel">
        <Alert severity="error">Failed to retrieve CloudFlex Integration options, please try again</Alert>
        <Button
          variant="outlined"
          color="secondary"
          style={{ marginTop: '16px', backgroundColor: '#ffffff' }}
          onClick={() => {
            triggerIntegrationsList();
          }}
        >
          Retry
        </Button>
      </div>
    );
  } else if (integrationsListApiClientStatus.clientStatus === ApiClientStatusEnum.Done) {
    const radioOptions = integrationsListApiClientStatus.data || [];
    isDeviceCreationAllowed = !!selectedIntegration.current && !!deviceName && !isCfCreationInProgress;

    PanelContent = (
      <>
        {cfCreationError && (
          <div className="fleet-actions__cloudflex-panel">
            <Alert
              severity="error"
              sx={{ width: '100%' }}
            >
              {cfCreationError}
            </Alert>
          </div>
        )}

        {newlyCreatedCfId && (
          <div className="fleet-actions__cloudflex-panel">
            <Alert
              severity="success"
              sx={{ width: '100%' }}
            >
              Successfully created new CloudFlex device &nbsp;
              <a href={`${selectedFleet}/devices/${newlyCreatedCfId}/device-details`}>{newlyCreatedCfId}</a>
            </Alert>
          </div>
        )}

        <FormControl fullWidth>
          {props.fleets ? (
            <>
              <InputLabel>Select fleet</InputLabel>

              <Select
                value={selectedFleet}
                input={<OutlinedInput label="Select fleet" />}
                onChange={({ target }) => setSelectedFleet(target.value)}
              >
                {(props.fleets || []).map(({ thingGroupName, thingGroupId }) => (
                  <MenuItem
                    key={thingGroupId}
                    value={thingGroupName}
                  >
                    {thingGroupName}
                  </MenuItem>
                ))}
              </Select>
            </>
          ) : (
            <div className="fleet-actions__cloudflex-loading-fleets">
              <CircularProgress size="1rem" />
              <span className="cloudflex-loading-label">&nbsp;Loading fleets...</span>
            </div>
          )}
        </FormControl>

        <FormControl
          component="fieldset"
          fullWidth
          sx={{ mt: 4 }}
        >
          <TextField
            value={deviceName}
            onChange={({ target }) => setDeviceName(target.value)}
            variant="outlined"
            required
            id="outlined-required"
            placeholder="Device name"
          />
        </FormControl>

        <FormControl
          component="fieldset"
          sx={{ mt: 4 }}
        >
          <FormLabel component="legend">Select CloudFlex integration type</FormLabel>
          <RadioGroup
            onChange={handleIntegrationSelection}
            value={selectedIntegrationOption}
          >
            {radioOptions.map((option) => (
              <FormControlLabel
                key={option.brand}
                value={option.brand}
                control={<Radio />}
                label={option.label}
              />
            ))}
          </RadioGroup>
        </FormControl>
      </>
    );
  }

  return (
    <>
      <Button
        variant="outlined"
        color="secondary"
        style={{ backgroundColor: '#ffffff' }}
        onClick={() => setShowCloudflexDialog(true)}
      >
        New CloudFlex
      </Button>

      <Dialog
        open={isShowCloudflexDialog}
        onClose={() => setShowCloudflexDialog(false)}
      >
        <DialogTitle>Create new CloudFlex device</DialogTitle>

        <DialogContent sx={{ overflow: 'visible', pt: 2 }}>{PanelContent}</DialogContent>

        <DialogActions>
          <Button
            onClick={() => createNewCloudFlexDevice(selectedIntegration.current!)}
            disabled={!isDeviceCreationAllowed}
          >
            Create
          </Button>
          <Button onClick={() => setShowCloudflexDialog(false)}>Cancel</Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
